import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { imageUrl } from '../lib/catalog';
import { PageIntro } from '../components/PageIntro';
import { ShimmerButton } from '../components/magicui/shimmer-button';

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, getCartTotal } = useCart();
  const navigate = useNavigate();

  if (cartItems.length === 0) {
    return (
      <div className="premium-page flex items-center justify-center">
        <div className="text-center px-5 max-w-xl">
          <ShoppingBag className="w-24 h-24 mx-auto mb-6 text-gray-300" />
          <h1 className="premium-title mb-5" data-testid="cart-empty-message">Vaša košarica je prazna</h1>
          <p className="text-gray-600 mb-8">Dodajte proizvode u košaricu kako biste nastavili kupovinu.</p>
          <ShimmerButton onClick={() => navigate('/webshop')} data-testid="empty-cart-shop-button">
            Nastavi s kupnjom
          </ShimmerButton>
        </div>
      </div>
    );
  }

  return (
    <div className="premium-page">
      <div className="premium-container">
        <PageIntro title="Vaš odabir." testId="cart-title" eyebrow="MAXLIGHT / Košarica" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div key={item.id} className="premium-cart-row" data-testid={`cart-item-${item.id}`}>
                <div className="grid grid-cols-[64px_minmax(0,1fr)_36px] items-center gap-x-3 gap-y-4 sm:flex sm:gap-6">
                  {/* Product Image */}
                  <Link to={`/product/${item.id}`} className="shrink-0" data-testid={`cart-product-image-link-${item.id}`}>
                    <img
                      src={imageUrl(item.image)}
                      alt={item.name}
                      data-testid={`cart-product-image-${item.id}`}
                      className="w-16 h-16 sm:w-24 sm:h-24 object-contain rounded"
                    />
                  </Link>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0 break-words">
                    <Link to={`/product/${item.id}`} data-testid={`cart-product-name-link-${item.id}`}>
                      <h3 className="text-base md:text-lg font-medium mb-1 hover:text-gray-600" data-testid={`cart-product-name-${item.id}`}>
                        {item.name}
                      </h3>
                    </Link>
                    <p className="text-gray-600 text-xs mb-2" data-testid={`cart-product-category-${item.id}`}>{item.category}</p>
                    <p className="text-xl font-bold" data-testid={`cart-unit-price-${item.id}`}>{item.price.toFixed(2)} €</p>
                  </div>

                  {/* Quantity Controls */}
                  <div className="col-start-2 row-start-2 flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      aria-label={`Smanji količinu: ${item.name}`}
                      data-testid={`cart-decrease-${item.id}`}
                      className="w-9 px-0 shrink-0"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-lg font-semibold w-8 text-center" data-testid={`cart-quantity-${item.id}`}>
                      {item.quantity}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      aria-label={`Povećaj količinu: ${item.name}`}
                      data-testid={`cart-increase-${item.id}`}
                      className="w-9 px-0 shrink-0"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Remove Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFromCart(item.id)}
                    className="col-start-3 row-start-1 w-9 px-0 shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    aria-label={`Ukloni: ${item.name}`}
                    data-testid={`cart-remove-${item.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </div>

                {/* Item Total */}
                <div className="mt-4 pt-4 border-t flex justify-between items-center">
                  <span className="text-gray-600">Ukupno za proizvod:</span>
                  <span className="text-xl font-bold" data-testid={`cart-item-total-${item.id}`}>
                    {(item.price * item.quantity).toFixed(2)} €
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="premium-summary lg:sticky lg:top-28">
              <h2 className="text-base md:text-lg font-medium mb-6" data-testid="cart-summary-heading">Sažetak narudžbe</h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Proizvodi ({cartItems.length}):</span>
                  <span className="font-semibold" data-testid="cart-subtotal">{getCartTotal().toFixed(2)} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Dostava:</span>
                  <span className="font-semibold" data-testid="cart-shipping">Prema ponudi</span>
                </div>
              </div>

              <div className="border-t pt-4 mb-6">
                <p className="text-xs text-gray-500 mb-2" data-testid="cart-vat-notice">Cijene uključuju 25 % PDV-a.</p>
                <div className="flex justify-between items-center">
                  <span className="text-xl font-semibold">Ukupno:</span>
                  <span className="text-2xl font-bold" data-testid="cart-total">{getCartTotal().toFixed(2)} €</span>
                </div>
              </div>

              <ShimmerButton
                onClick={() => navigate('/checkout')}
                data-testid="cart-checkout-button"
                className="w-full mb-4"
              >
                Nastavi na naplatu
              </ShimmerButton>

              <Button
                onClick={() => navigate('/webshop')}
                data-testid="cart-continue-shopping-button"
                variant="outline"
                size="lg"
                className="w-full"
              >
                Nastavi s kupnjom
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
